import type { SFC, CSSProperties } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { window } from '../../globals';
import Icon, { icons } from './Icon';
import Link, { SupportLink } from './Link';
import Text from './Text';

const iconLargeStyles: CSSProperties = {
  fontSize: '60px',
};

type AppErrorProps = {
  heading: string;
  text: string;
  isLoggedIn: boolean;
  displayContactLink?: boolean;
};

/**
 * A generic, top-level app error.
 * For things like NotFound or ErrorBoundary-type errors.
 *
 * @param {AppErrorProps} props - AppError props
 * @param {string} props.heading - The main/heading text.
 * @param {string} props.text - Some text explaining the error.
 * @param {boolean} [props.isLoggedIn] - If the user is logged in or not (window.user)
 */
const AppError: SFC<AppErrorProps> = ({ heading, text, isLoggedIn, displayContactLink }) => (
  <div className="text-center container margin-top">
    <Row>
      <Col md={6} mdOffset={3} className="card">
        <span style={iconLargeStyles}>
          <Icon icon={icons.remove} />
        </span>
        <Text.H2>{heading}</Text.H2>
        <p>{text}</p>
        {displayContactLink && (
          <p>
            <SupportLink>Contact support</SupportLink> if this error recurs.
          </p>
        )}
        <AppErrorLink isLoggedIn={isLoggedIn} />
      </Col>
    </Row>
  </div>
);

/**
 * <Link /> for the AppError, which is different depending on if the user is logged in or not.
 *
 * @param {boolean} isLoggedIn - If the user is logged in or not (window.user)
 */
const AppErrorLink: SFC<{ isLoggedIn: boolean }> = ({ isLoggedIn }) => isLoggedIn
  ? (
    <Link to="/">
      <Button bsStyle="primary">
        &laquo; Go to Home
      </Button>
    </Link>
    )
  : (
    <Link to={`/auth/login?next=${encodeURIComponent(window.location.pathname)}`} className="btn btn-primary">
      <Icon icon={icons.lock} /> Login to FOSSA
    </Link>
    );

const GENERIC_ERROR_MESSAGE = window.on_prem
  ? 'Something has gone wrong.'
  : 'Something has gone wrong. Our team has been notified, and we are looking into it.';

/**
 *  A generic app-wide error, with a standardized message and Contact Support link.
 */
export const GenericAppError = () => (
  <AppError
    heading="Error"
    text={GENERIC_ERROR_MESSAGE}
    isLoggedIn={!!window.user}
    displayContactLink
  />
);

export default AppError;
